import React, {useCallback, useState} from 'react'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Variant} from '@vio/atlas/src/components/designSystem/Button/Button'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {filterAllowedCodes} from '@vio/core/src/brand/utils'
import availableLanguages from '@vio/core/src/localization/services/locale'
import {CurrencyType} from '@vio/core/src/localization/types/CurrencyType'

import {LanguageCurrencySelector} from '../../../search/LanguageCurrencySelector'
import {PreferencesLink} from './PreferencesLink'

export type SelectedElement = 'language' | 'currency'

type PreferencesFormValues = {
  language: string
  currency: string
}
interface ChangedPreferences {
  hasCurrencyChanged: boolean
  hasLanguageChanged: boolean
  currency: string
  language: string
}

interface PreferencesWidgetProps {
  /** Currently selected currency */
  currentCurrency: string
  /** Currently selected language */
  currentLanguage: string
  /** List of currently enabled currencies */
  enabledCurrencies: CurrencyType[]
  /** Should it render the currency */
  hasCurrency?: boolean
  /** Should it render the language */
  hasLanguage?: boolean
  /** Callback action that should be called when preferences change */
  onPreferencesChange: (changedPreferences: ChangedPreferences) => void
  /** Callback action that should be called when preferences link is clicked */
  onPreferencesLinkClick: () => void
  /** Callback action that should be called when preferences select input is clicked */
  onSelectClick?: (selectedElement: SelectedElement) => void
  /** Callback action that should be called when preferences select input is clicked */
  showPriceDisplaySetting?: boolean
  /** Callback action that should be called when preferences select input is clicked */
  showTotalPrices?: boolean
  /** Callback action that should be called when preferences select input is clicked */
  onChangePriceDisplay?: (showTotal: boolean) => void
  /** Should it render the include taxes */
  includeTaxes?: boolean
  /** Preferences button variant */
  preferencesButtonVariant?: Variant
}

export const PreferencesWidget = ({
  currentCurrency,
  currentLanguage,
  enabledCurrencies,
  hasCurrency = true,
  hasLanguage = true,
  showPriceDisplaySetting = false,
  showTotalPrices = false,
  includeTaxes = false,
  preferencesButtonVariant,
  onChangePriceDisplay,
  onPreferencesChange,
  onPreferencesLinkClick,
  onSelectClick
}: PreferencesWidgetProps) => {
  const {isMobile} = useDeviceLayout()
  const {brand} = useBrand()

  const [initialSelector, setInitialSelector] = useState<SelectedElement>(
    hasLanguage ? 'language' : 'currency'
  )
  const [IsPreferencePopupOpen, setIsPreferencePopupFormOpen] = useState(false)

  const handlePreferencesChange = useCallback(
    async ({currency, language}: PreferencesFormValues) => {
      const hasCurrencyChanged = currency !== currentCurrency
      const hasLanguageChanged = language !== currentLanguage

      if (hasCurrencyChanged || hasLanguageChanged) {
        onSelectClick?.(hasCurrencyChanged ? 'currency' : 'language')
        onPreferencesChange({
          hasCurrencyChanged,
          hasLanguageChanged,
          currency,
          language
        })
        if (hasCurrencyChanged) {
          setIsPreferencePopupFormOpen(false)
        }
      } else {
        setIsPreferencePopupFormOpen(false)
      }
    },
    [currentCurrency, currentLanguage, onPreferencesChange, onSelectClick]
  )

  const handlePreferencesLinkClick = useCallback(
    (element: SelectedElement) => {
      setIsPreferencePopupFormOpen(true)
      setInitialSelector(element)
      onPreferencesLinkClick()
    },
    [onPreferencesLinkClick]
  )

  const handleChangePriceDisplay = useCallback(
    (showTotal: boolean) => {
      onChangePriceDisplay?.(showTotal)
      setIsPreferencePopupFormOpen(false)
    },
    [onChangePriceDisplay]
  )

  const languages = hasLanguage
    ? filterAllowedCodes(availableLanguages, brand.languageCodes)
    : []

  return (
    <>
      <PreferencesLink
        currency={currentCurrency}
        language={currentLanguage}
        hasCurrency={hasCurrency}
        hasLanguage={hasLanguage}
        onClick={handlePreferencesLinkClick}
        buttonVariant={preferencesButtonVariant}
      />
      <LanguageCurrencySelector
        isMobile={isMobile}
        isOpen={IsPreferencePopupOpen}
        onClose={() => setIsPreferencePopupFormOpen(false)}
        availableLanguages={languages}
        availableCurrencies={filterAllowedCodes(
          enabledCurrencies,
          brand.currencyCodes
        )}
        selectedLanguage={currentLanguage}
        selectedCurrency={currentCurrency}
        initialSelector={initialSelector}
        includeTaxes={includeTaxes}
        hasCurrency={hasCurrency}
        onChangeCurrency={currency =>
          handlePreferencesChange({currency, language: currentLanguage})
        }
        onChangeLanguage={language =>
          handlePreferencesChange({currency: currentCurrency, language})
        }
        showPriceDisplaySetting={showPriceDisplaySetting}
        showTotalPrices={showTotalPrices}
        onChangePriceDisplay={handleChangePriceDisplay}
      />
    </>
  )
}
